import { removeNamespace } from '@/utils/helpers'
import { queryCTF, getCTFAsset, sortCTFEntries } from '@/plugins/contentful'
import { getUrlOverride, hasUrlOverride } from './url-overrides'
import Vue from 'vue'

export const SITE_TYPES = {
  getters: {
    defaultPage: 'site/defaultPage'
  },
  mutations: {
    SET_SITE_CONFIG: 'site/SET_SITE_CONFIG',
    SET_CONFIG_ASSETS: 'site/SET_CONFIG_ASSETS'
  },
  actions: {
    loadSiteConfig: 'site/loadSiteConfig'
  }
}

const _types = removeNamespace('site', SITE_TYPES)

const state = () => ({
  siteConfig: null,
  configAssets: null
})

const getters = {
  [_types.getters.defaultPage] (state) {
    return state.siteConfig.pages.find(l => l.default)
  }
}

const mutations = {
  [_types.mutations.SET_SITE_CONFIG] (state, pages) {
    state.siteConfig = pages
  },
  [_types.mutations.SET_CONFIG_ASSETS] (state, assets) {
    state.configAssets = assets
  }
}

const actions = {
  async [_types.actions.loadSiteConfig] ({ commit }) {
    let host = window.location.origin
      ? window.location.origin
      : window.location.protocol + '/' + window.location.host

    // Forcing local development host. Won't impact live site:
    host = host.includes('localhost') ? process.env.VUE_APP_DEV_URL_FORCE : host
    let { items, includes } = await queryCTF(
      `&include=1&content_type=siteConfig&select=fields,sys.id&fields.siteBaseUrl=${host}`, false
    )

    const groupSiteConfigs = items[0].fields.groupSiteConfigs ? items[0].fields.groupSiteConfigs : null
    let isGroupConfig = false
    let siteBaseUrl
    if (groupSiteConfigs !== null) {
      const amountOfConfigs = groupSiteConfigs.length ? groupSiteConfigs.length : 0

      if (amountOfConfigs > 0) {
        siteBaseUrl = window.location.href

        if (window.location.pathname.split('/').length > 1) {
          siteBaseUrl = '/' + window.location.pathname.split('/')[1]
        }

        for (let i = 0; i < amountOfConfigs; i++) {
          const url = window.location.origin + siteBaseUrl
          const response = await queryCTF(`&include=1&content_type=groupSiteConfig&fields.siteBaseUrl=${url}`, false)

          if (response.items.length !== 0) {
            isGroupConfig = true
            items = response.items
            includes = response.includes
          }
        }
      }
    }

    if (items && includes && items.length && includes.Entry) {
      const { fields } = items[0]

      if (fields.secondaryColor === 'dark blue') {
        Vue.prototype.$vuetify.theme.themes.light.secondary = '#033f60'
      }
      const defaultEntry = includes.Entry.find(
        e => e.sys.id === fields.defaultPage.sys.id
      )

      let route
      let pageRoute = '/'

      if (defaultEntry && isGroupConfig) {
        route = siteBaseUrl + '/'

        if (window.location.pathname.split('/').length > 1) {
          pageRoute += window.location.pathname.split('/')[1]
        }
      }

      const sortedPages = sortCTFEntries(fields.pages, includes.Entry)
      fields.pages = [
        {
          default: true,
          id: defaultEntry.sys.id,
          text: defaultEntry.fields.name,
          navLink: false,
          route: pageRoute,
          updatedAt: defaultEntry.sys.updatedAt
        }
      ].concat(
        (sortedPages && sortedPages.length)
          ? [...sortedPages]
            .filter(e => e.sys.id !== defaultEntry.sys.id)
            .map(e => {
              if (hasUrlOverride(e.fields.name)) {
                return {
                  default: false,
                  id: e.sys.id,
                  text: e.fields.name,
                  navLink: e.fields.navLink,
                  route:
                    (route ?? '') +
                    getUrlOverride(e.fields.name).valueOf(),
                  updatedAt: e.sys.updatedAt
                }
              } else {
                return {
                  default: false,
                  id: e.sys.id,
                  text: e.fields.name,
                  navLink: e.fields.navLink,
                  route:
                    (route ?? '/') +
                    encodeURIComponent(
                      e.fields.name.replace(/\s/g, '-').toLowerCase()
                    ),
                  updatedAt: e.sys.updatedAt
                }
              }
            })
          : []
      )
      if (includes.Asset) {
        commit(_types.mutations.SET_CONFIG_ASSETS, includes.Asset)
        if (fields.navigationLogo) {
          const navLogo = getCTFAsset(fields.navigationLogo.sys.id, includes.Asset)
          if (navLogo) {
            fields.navigationLogo = {
              title: navLogo.fields.title,
              url: navLogo.fields.file.url
            }
          }
        }
        if (fields.footerLogo) {
          const footerLogo = getCTFAsset(fields.footerLogo.sys.id, includes.Asset)
          if (footerLogo) {
            fields.footerLogo = {
              title: footerLogo.fields.title,
              url: footerLogo.fields.file.url
            }
          }
        }
      }
      const siteConfig = { ...fields, id: items[0].sys.id }
      commit(_types.mutations.SET_SITE_CONFIG, siteConfig)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
